import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Mail } from 'lucide-react';
interface ResultsScreenProps {
  onStartOver: () => void;
}
function AnswerItem({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-43">
      <div className="text-brand-accent text-xs font-bold uppercase tracking-wider mb-0.5" data-id="element-44">{label}</div>
      <div className="text-brand-dark text-sm leading-snug" data-id="element-45">{value}</div>
    </div>;
}
function SectionDivider({
  label
}: {
  label: string;
}) {
  return <div className="w-full px-4 py-5 flex items-center gap-3" data-id="element-46">
      <div className="flex-1 h-px bg-brand-dark/20" data-id="element-47" />
      <span className="text-brand-dark/40 text-xs font-bold uppercase tracking-widest whitespace-nowrap" data-id="element-48">{label}</span>
      <div className="flex-1 h-px bg-brand-dark/20" data-id="element-49" />
    </div>;
}
function CollapsibleOutput({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(true);
  return <div className="px-4 pb-6" data-id="element-50">
      <button onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between gap-3 mb-4" aria-expanded={open} data-id="element-51">
        <h2 className="text-xl font-bold text-brand-dark text-left" data-id="element-52">{title}</h2>
        <div className="text-brand-dark flex-shrink-0" data-id="element-53">
          {open ? <ChevronUp size={20} data-id="element-54" /> : <ChevronDown size={20} data-id="element-55" />}
        </div>
      </button>

      {open && <div className="space-y-5 text-sm leading-relaxed text-brand-dark" data-id="element-56">
          {children}
        </div>}
    </div>;
}
function OutputBlock({
  heading,
  children
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-57">
      <div className="text-xs font-bold uppercase tracking-wider text-brand-dark/50 mb-1" data-id="element-58">{heading}</div>
      <div data-id="element-59">{children}</div>
    </div>;
}
export function ResultsScreen({
  onStartOver
}: ResultsScreenProps) {
  return <div className="flex flex-col w-full min-h-screen bg-brand-surface text-brand-dark pb-16" data-id="element-60">

      <div className="px-4 pt-10 pb-6 bg-brand-dark" data-id="element-61">
        <h1 className="text-3xl font-bold text-white mb-1" data-id="element-62">Your Founder's Idea Kit</h1>
        <p className="text-brand-accent font-medium text-sm" data-id="element-63">Built from your answers today.</p>
      </div>

      <div className="mx-4 mt-5 mb-2 bg-brand-accent/10 border border-brand-accent/30 rounded-xl p-4 flex items-start gap-3" data-id="element-64">
        <div className="flex-shrink-0 mt-0.5" data-id="element-65">
          <Mail size={18} className="text-brand-accent" data-id="element-66" />
        </div>
        <div data-id="element-67">
          <p className="text-brand-dark font-bold text-sm" data-id="element-68">Check your inbox.</p>
          <p className="text-brand-dark/70 text-sm mt-0.5" data-id="element-69">
            Your full Founder's Idea Kit, all three outputs, has been sent to your email. Everything below is a preview.
          </p>
        </div>
      </div>

      <div className="px-4 pt-6 pb-4" data-id="element-70">
        <div className="text-xs font-bold uppercase tracking-widest text-brand-dark/40 mb-3" data-id="element-71">Your Brief</div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-black/5 space-y-4" data-id="element-72">
          <AnswerItem label="Idea" value="A platform that helps companies see their carbon emissions across their entire supply chain." data-id="element-73" />
          <AnswerItem label="User" value="Sustainability leads and ESG directors at large companies with complex, multi-input supply chains." data-id="element-74" />
          <AnswerItem label="Pain point" value="They want to be more sustainable but can't track emissions due to the volume of data involved." data-id="element-75" />
          <AnswerItem label="Workaround" value="They seek vendor support or make manual assumptions based on incomplete data." data-id="element-76" />
          <AnswerItem label="Solution" value="Centralizes all supply chain data, collects emissions across every input, and cross-references with historical benchmark data." data-id="element-77" />
          <AnswerItem label="Outcome" value="Better decisions on sustainability, efficiency, supply chain predictability, and cost reduction." data-id="element-78" />
          <AnswerItem label="Uncertainty" value="There are a lot of competitors. Differentiation will be key to standing out." data-id="element-79" />
        </div>
      </div>

      <SectionDivider label="Output 1" data-id="element-80" />

      <CollapsibleOutput title="Validation Doc" data-id="element-81">
        <OutputBlock heading="Research Objective" data-id="element-82">
          Determine whether sustainability leads at large enterprises find current emissions tracking painful enough to replace their existing vendor relationships for a purpose-built platform.
        </OutputBlock>
        <OutputBlock heading="User Recruitment" data-id="element-83">
          Recruit sustainability leads or ESG directors at companies with 500+ employees and multi-tier supply chains. Open with: "I'm researching how sustainability teams handle emissions tracking today. I'd love to understand your current process before we talk about anything else."
        </OutputBlock>
        <OutputBlock heading="Section 1: Pain Probe" data-id="element-84">
          Walk me through the last time you had to report on your supply chain emissions. What did that process actually look like?
          <p className="italic text-brand-dark/60 mt-1" data-id="element-85">(If they go surface-level: What was the most time-consuming part of pulling that together?)</p>
        </OutputBlock>
        <OutputBlock heading="Section 2: Workaround Probe" data-id="element-86">
          What do you do today when you need emissions data from a vendor who doesn't track it?
          <p className="italic text-brand-dark/60 mt-1" data-id="element-87">(If they go surface-level: How confident are you in the numbers you end up reporting?)</p>
        </OutputBlock>
        <OutputBlock heading="Section 3: Solution Fit" data-id="element-88">
          If you had a single platform that gave you real-time emissions visibility across every supplier, what would that change about how you work?
          <p className="italic text-brand-dark/60 mt-1" data-id="element-89">(If they go surface-level: What decision would you finally be able to make that you can't make today?)</p>
        </OutputBlock>
        <OutputBlock heading="Section 4: Assumption Test" data-id="element-90">
          When you think about switching from your current approach to a dedicated platform, what would have to be true for that to be worth it?
          <p className="italic text-brand-dark/60 mt-1" data-id="element-91">(If they go surface-level: What would make you choose one platform over another in this space?)</p>
        </OutputBlock>
        <OutputBlock heading="Closing" data-id="element-92">
          Is there anything about how your team handles sustainability reporting that I haven't asked about but should understand?
        </OutputBlock>
      </CollapsibleOutput>

      <SectionDivider label="Output 2" data-id="element-93" />

      <CollapsibleOutput title="Vibe Coding Prompts" data-id="element-94">
        <div className="bg-white border border-brand-accent/20 rounded-xl p-4" data-id="element-95">
          <div className="text-xs font-bold uppercase tracking-wider text-brand-accent mb-2" data-id="element-96">Context Wrapper</div>
          <p data-id="element-97">I'm building a supply chain emissions tracking platform for sustainability leads and ESG directors at large enterprises. The core problem is that these teams can't get accurate emissions data across their supply chains because the data lives in too many places and vendors report inconsistently. My product centralizes all of that data, collects emissions inputs across the full supply chain, and cross-references everything against historical benchmarks. The end state for my user is confident, defensible sustainability reporting and the ability to make real decisions about supplier relationships based on actual data.</p>
        </div>
        <OutputBlock heading="Prompt 1: Project Setup" data-id="element-98">
          Using the context above, set up the basic structure of a web dashboard application. No features yet. Just the shell. It should have a top navigation bar, a sidebar for main sections, and a main content area. The visual style should feel enterprise-appropriate: clean, data-focused, professional.
        </OutputBlock>
        <OutputBlock heading="Prompt 2: Core User Flow" data-id="element-99">
          Using the context above, build the first screen a sustainability lead sees when they log in. It should show an overview of their supply chain emissions status: what's been reported, what's still missing, and where the biggest gaps are. Use realistic placeholder data.
        </OutputBlock>
        <OutputBlock heading="Prompt 3: Core Functionality" data-id="element-100">
          Using the context above, build the emissions data collection interface. This is where the product does its job: aggregating supplier data, flagging inconsistencies, and cross-referencing inputs against benchmark data. Show how incomplete or estimated data is handled differently from verified data.
        </OutputBlock>
        <OutputBlock heading="Prompt 4: Outcome State" data-id="element-101">
          Using the context above, build the reporting view. The screen that shows what the user has when everything is working. This is a sustainability report dashboard with real numbers, trend lines, and supplier-level breakdowns. This is the destination: the view that proves the product's value.
        </OutputBlock>
        <OutputBlock heading="Prompt 5: First Validation Step" data-id="element-102">
          Using the context above, strip everything back to one screen. Build a single supplier emissions comparison view that shows two or three suppliers side by side with their reported vs. estimated emissions. Mock it with realistic data. The goal is to put this in front of a real sustainability lead and ask: "Would this help you make a supplier decision?" Nothing else needs to work.
        </OutputBlock>
      </CollapsibleOutput>

      <SectionDivider label="Output 3" data-id="element-103" />

      <CollapsibleOutput title="UX Playbook" data-id="element-104">
        <OutputBlock heading="Product" data-id="element-105">
          microWatt is a supply chain emissions intelligence platform built for enterprise sustainability teams. It centralizes emissions data across all supplier inputs and cross-references it against historical benchmarks to surface the gaps and inconsistencies that manual processes miss.
        </OutputBlock>
        <OutputBlock heading="User" data-id="element-106">
          The primary user is a sustainability lead or ESG director at a large company with a complex, multi-tier supply chain. They own emissions reporting, manage vendor sustainability requirements, and are accountable for the company's public sustainability commitments, often without the data infrastructure to back them up.
        </OutputBlock>
        <OutputBlock heading="Before State" data-id="element-107">
          Today, this person spends weeks before every reporting cycle chasing vendors for data, manually reconciling inconsistent formats, and making assumptions where data simply doesn't exist. Their current workaround, relying on vendor self-reporting or third-party estimates, produces numbers they can't fully defend, which creates risk every time a report goes out.
        </OutputBlock>
        <OutputBlock heading="After State" data-id="element-108">
          With microWatt, they open a single dashboard and see their entire supply chain emissions picture in one place. They can identify which suppliers are underreporting, run scenarios on potential changes, and produce defensible reports without a manual data reconciliation process.
        </OutputBlock>
        <OutputBlock heading="Core Flow" data-id="element-109">
          <ol className="list-decimal pl-4 space-y-1.5 mt-1" data-id="element-110">
            <li data-id="element-111">Connect supply chain data sources and existing vendor inputs to the platform.</li>
            <li data-id="element-112">Review the emissions gap analysis: which suppliers have verified data vs. estimates.</li>
            <li data-id="element-113">Investigate flagged suppliers with inconsistent or missing emissions data.</li>
            <li data-id="element-114">Run a scenario to model the impact of switching a high-emission supplier.</li>
            <li data-id="element-115">Export a finalized emissions report with data source attribution for each input.</li>
          </ol>
        </OutputBlock>
        <OutputBlock heading="What to Design First" data-id="element-116">
          The highest-priority design question is differentiation: what makes this platform's interface noticeably better than the spreadsheet-and-vendor-email workflow it's replacing. Start by designing the supplier comparison view, the screen where a user can see two or three suppliers side by side with their emissions data, confidence levels, and recommended actions. This is the screen that proves the product is worth switching for.
        </OutputBlock>
        <OutputBlock heading="Open Questions" data-id="element-117">
          <ol className="list-decimal pl-4 space-y-1.5 mt-1" data-id="element-118">
            <li data-id="element-119">How does the platform handle suppliers who refuse to share emissions data directly?</li>
            <li data-id="element-120">What level of benchmark data accuracy is defensible enough for public sustainability reporting?</li>
            <li data-id="element-121">Does the platform need to support multiple reporting frameworks (GHG Protocol, CDP, etc.) or is one framework enough to start?</li>
            <li data-id="element-122">Who else in the organization needs access to this data: procurement, finance, legal?</li>
          </ol>
        </OutputBlock>
      </CollapsibleOutput>

      <div className="px-4 pt-4 pb-12 text-center" data-id="element-123">
        <div className="w-full h-px bg-brand-dark/10 mb-8" data-id="element-124" />
        <p className="font-bold text-base mb-8" data-id="element-125">This framework is reusable. Same seven questions. Any idea.</p>
        <button onClick={onStartOver} className="w-full bg-brand-accent text-white py-4 rounded-xl font-bold text-lg hover:bg-brand-accent/90 transition-colors shadow-lg" data-id="element-126">
          Start Over
        </button>
      </div>
    </div>;
}